import React, { useCallback, useEffect, useState } from 'react';
import { Highlight, themes } from 'prism-react-renderer';
import { Copy } from 'lucide-react';
import toast from 'react-hot-toast';

const useDocsDarkMode = () => {
    const [isDarkMode, setIsDarkMode] = useState(() => {
        if (typeof document === 'undefined') return true;
        return !document.body.classList.contains('light-mode');
    });

    useEffect(() => {
        if (typeof document === 'undefined') return undefined;
        const sync = () => setIsDarkMode(!document.body.classList.contains('light-mode'));
        const observer = new MutationObserver(sync);
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);

    return isDarkMode;
};

/**
 * Syntax-highlighted code block for feature docs pages.
 */
export default function DocsCodeBlock({ code, language = 'javascript', showCopy = true }) {
    const isDarkMode = useDocsDarkMode();

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(code);
            toast.success('Copied to clipboard');
        } catch {
            toast.error('Could not copy');
        }
    }, [code]);

    return (
        <div
            style={{
                position: 'relative',
                backgroundColor: 'var(--input-bg)',
                border: '1px solid var(--border-primary)',
                borderRadius: 8,
                margin: '0 0 16px',
                overflow: 'hidden',
            }}
        >
            {showCopy && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 8px 0' }}>
                    <button
                        type="button"
                        onClick={() => void handleCopy()}
                        style={{
                            backgroundColor: 'transparent',
                            border: '1px solid var(--border-primary)',
                            borderRadius: 6,
                            padding: 6,
                            color: 'var(--text-secondary)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                        }}
                        title="Copy to clipboard"
                        aria-label="Copy code to clipboard"
                    >
                        <Copy size={14} />
                    </button>
                </div>
            )}
            <div
                style={{
                    padding: showCopy ? '0 16px 16px' : '16px',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: 13,
                    lineHeight: 1.5,
                    overflow: 'auto',
                }}
            >
                <Highlight
                    key={`${language}-${isDarkMode}`}
                    theme={isDarkMode ? themes.vsDark : themes.gruvboxMaterialLight}
                    code={code.trimEnd()}
                    language={language}
                >
                    {({ className, style, tokens, getLineProps, getTokenProps }) => (
                        <pre className={className} style={{ ...style, margin: 0, backgroundColor: 'transparent' }}>
                            {tokens.map((line, i) => (
                                <div key={i} {...getLineProps({ line })}>
                                    {line.map((token, key) => (
                                        <span key={key} {...getTokenProps({ token })} />
                                    ))}
                                </div>
                            ))}
                        </pre>
                    )}
                </Highlight>
            </div>
        </div>
    );
}
